import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FC } from "react";
import { Box, CircularProgress, TextField } from "@mui/material";
import styles from "./Authorization.module.css";
import { getRequest } from "shared/utils/fetch-api";
import { useForm } from "shared/utils/hooks/useForm";
import { validateForm } from "shared/utils/validate-form";
import {
  emailValidationSchema,
  validationSchema,
} from "features/auth/utils/validationSchema";
import { useTimer } from "shared/utils/useTimer";
import CustomButton from "shared/custom-button/CustomButton";
import UgskLogoWhite from "shared/assets/images/UgskLogoWhite";
import AuthFon from "shared/assets/AuthFon";
import { resetAppDrawer } from "entities/app-drawer";
import { useAppDispatch } from "shared/utils/hooks/useRedux";

interface IErrorCode {
  Msg: string;
}

export const Authorization: FC = () => {
  const [errorsCode, setErrorsCode] = useState<IErrorCode[] | []>([]);
  const [success, setSuccess] = useState(false);
  const [isSend, setIsSend] = useState(false);
  const [errorCodeAuth, setErrorCodeAuth] = useState<IErrorCode[] | []>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [messageCodeAuth, setMessageCodeAuth] = useState<{
    code: string;
    message: string;
  }>({ code: "", message: "" });
  const [limitAuth, setLimitAuth] = useState(false);
  const [messageLimit, setMessageLimit] = useState<{
    message: string;
    time: string;
  }>({ message: "", time: "" });
  const [staticFormattedTime, setStaticFormattedTime] = useState<string | null>(
    null
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userData = {
    Email: "",
    OneTimePassword: "",
  };
  const { formData, handleChangeInput, errors, setErrors, handleBlurInput } =
    useForm(userData, validationSchema);

  const initialTime = 6 * 60;
  const { time, resetTimer } = useTimer(initialTime);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (success && time !== 0 && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [success, time]);

  const getCode = async (): Promise<void> => {
    const errors = await validateForm(
      { Email: formData.Email },
      emailValidationSchema
    );
    if (!errors.Email) {
      try {
        setIsLoading(true);
        const response = await getRequest(
          {
            Email: formData.Email,
          },
          "dms-hr",
          "getPass",
          "POST"
        );
        const result = await response.json();
        if (result.Errors.length > 0) {
          setErrorCodeAuth(result.Errors);
        } else {
          setErrorCodeAuth([]);
          setSuccess(true);
          setIsSend(true);
          resetTimer();
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsSend(false);
        setIsLoading(false);
      }
    } else {
      setErrors({ Email: errors.Email });
    }
  };

  useEffect(() => {
    const firstError = errorCodeAuth.find((obj: IErrorCode) =>
      obj.Msg.startsWith("Найдено более одного застрахованного")
    );
    const secondError = errorCodeAuth.find((obj: IErrorCode) =>
      obj.Msg.startsWith("Не найден застрахованный по электронной почте")
    );
    const thirdError = errorCodeAuth.find((obj: IErrorCode) =>
      obj.Msg.startsWith(
        "Ошибка при отправке. Текст ошибки: {Документ.ЭлПочта_ИсходящееСообщение.МодульМенеджера(190)}: Error calling context method (Подключиться): SMTP error. Error code: 25 (The server rejected the connection.)"
      )
    );
    if (firstError) {
      setMessageCodeAuth({
        code: "001",
        message: `${firstError.Msg}. Обратитесь к административному куратору`,
      });
    } else if (secondError) {
      setMessageCodeAuth({ code: "002", message: secondError.Msg });
    } else if (thirdError) {
      setMessageCodeAuth({
        code: "003",
        message: "Ошибка отправки кода на почту. Пожалуйста, попробуйте позже",
      });
    }
  }, [errorCodeAuth]);

  const getAuth = async (): Promise<void> => {
    const errors = await validateForm(formData, validationSchema);
    if (
      errors &&
      Object.keys(errors).length === 0 &&
      Object.values(errors).every((value) => value === null)
    ) {
      try {
        const response = await getRequest(
          {
            Email: formData.Email,
            OneTimePassword: formData.OneTimePassword,
          },
          "dms-hr",
          "getAuth",
          "POST"
        );
        const result = await response.json();
        if (result.Errors.length > 0) {
          setErrorsCode(result.Errors);
          setErrors({ OneTimePassword: "Введен неверный код" });
          const limitFind: { Msg: string } = result.Errors.find(
            (obj: { Msg: string }) =>
              obj.Msg.startsWith(
                "Превышено максимальное количество попыток авторизации по электронной почте"
              )
          );
          if (limitFind) {
            setLimitAuth(true);
            const messageLimitArray = limitFind.Msg.split(";");
            setMessageLimit({
              message: messageLimitArray[0],
              time: messageLimitArray[1],
            });
            setStaticFormattedTime(
              formatDateAfterAddingSeconds(messageLimitArray[1])
            );
          }
        } else {
          dispatch(resetAppDrawer());
          navigate("/insurance-contracts", {
            state: {
              textRout: "insurance-contracts",
              name: "Договоры страхования",
            },
          });
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      setErrors(errors);
    }
  };

  const formatDateAfterAddingSeconds = (seconds: string): string => {
    const secondsToAdd = parseInt(seconds, 10);
    const currentDate = new Date();
    const resultDate = new Date(currentDate.getTime() + secondsToAdd * 1000);
    const formattedDate = `${String(resultDate.getDate()).padStart(2, "0")}.${String(resultDate.getMonth() + 1).padStart(2, "0")}.${resultDate.getFullYear()}`;
    const formattedTime = `${String(resultDate.getHours()).padStart(2, "0")}:${String(resultDate.getMinutes()).padStart(2, "0")}`;
    return `Доступ заблокирован до: ${formattedDate}, ${formattedTime}`;
  };

  const handleClick = (event: any) => {
    event.preventDefault();
    getCode();
  };

  return (
    <Box className={styles.authBody}>
      <Box className={styles.authBoxFon}>
        <AuthFon />
      </Box>
      <Box className={styles.contentForm}>
        <Box className={styles.authBoxContainer}>
          <Box className={styles.logo}>
            <UgskLogoWhite />
          </Box>
          <Box className={styles.authBox}>
            <Box className={styles.titleBox}>
              <h1 className={styles.unboundedTitle}>Вход в личный кабинет</h1>
            </Box>
            <form id="Auth">
              <Box className={styles.formBox}>
                <TextField
                  name="Email"
                  label="Введите рабочий email"
                  variant="outlined"
                  className={styles.emailInput}
                  value={formData.Email}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeInput({ event })
                  }
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                    handleBlurInput(event)
                  }
                  error={!!errors[`Email`]}
                  helperText={errors[`Email`] || ""}
                />
                {(!success || (success && time === 0)) && (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      <CustomButton
                        title="Запросить код"
                        handleClick={getCode}
                        textColor="white"
                        backgroundColor="#326CFD"
                        width="350px"
                        gap="10px"
                        borderRadius="4px"
                        padding="3px"
                      />
                    )}
                  </>
                )}
                {errorCodeAuth.length > 0 && (
                  <Box className={`${styles.textBox} ${styles.redText}`}>
                    <p>{messageCodeAuth.message}</p>
                  </Box>
                )}
                {success && time !== 0 && (
                  <>
                    <TextField
                      name="OneTimePassword"
                      label="Введите код"
                      type="number"
                      variant="outlined"
                      className={styles.emailInput}
                      value={formData.OneTimePassword}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeInput({ event })
                      }
                      onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                        handleBlurInput(event)
                      }
                      error={!!errors[`OneTimePassword`]}
                      helperText={errors[`OneTimePassword`] || ""}
                      inputRef={codeInputRef}
                    />
                    <CustomButton
                      title="Войти"
                      handleClick={getAuth}
                      textColor="white"
                      backgroundColor="#326CFD"
                      width="350px"
                      gap="10px"
                      borderRadius="4px"
                      padding="3px"
                    />
                  </>
                )}
              </Box>
              {success && limitAuth && (
                <Box className={`${styles.textBox} ${styles.redText}`}>
                  <p>{messageLimit.message}</p>
                  <p>{staticFormattedTime}</p>
                </Box>
              )}
              {success && !limitAuth && (
                <Box className={styles.textBox}>
                  {time !== 0 ? (
                    <>
                      <p>На вашу почту был отправлен код для авторизации</p>
                      <p>
                        {Math.floor(time / 60)
                          .toString()
                          .padStart(2, "0")}
                        :{(time % 60).toString().padStart(2, "0")}
                      </p>
                      <p>до окончания действия кода</p>
                    </>
                  ) : (
                    <p>Время действия кода закончилось! Запросите новый код.</p>
                  )}
                </Box>
              )}
            </form>
          </Box>
          {success && !limitAuth && (
            <Box className={styles.dopBtn}>
              <p>
                Код не пришел?{" "}
                <a href="" onClick={handleClick}>
                  Запросить новый
                </a>
              </p>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Authorization;
